/**
 * Phase 8 T-06b · Overlay registry for Esc-to-top-of-stack behaviour.
 *
 * When multiple overlays (command palette, Assistant slide-over, modals) are
 * open at once, a global `Esc` handler should close only the most recently
 * opened one. This tiny module records each open overlay with an opened-at
 * timestamp so callers can close the top of the stack deterministically.
 *
 * This is intentionally module-level (not React context) so non-rendering
 * code — like the CommandPaletteProvider keydown listener — can read the
 * stack without prop drilling.
 */

type CloseFn = () => void;

interface OverlayEntry {
  id: string;
  close: CloseFn;
  openedAt: number;
}

const overlays = new Map<string, OverlayEntry>();

/** Register an overlay as open. Idempotent: calling twice updates openedAt. */
export function registerOverlay(id: string, close: CloseFn): void {
  overlays.set(id, { id, close, openedAt: Date.now() });
}

/** Remove an overlay from the registry (call on close). */
export function unregisterOverlay(id: string): void {
  overlays.delete(id);
}

/** Return the id of the most recently opened overlay, or null. */
export function topOverlayId(): string | null {
  let top: OverlayEntry | null = null;
  for (const e of overlays.values()) {
    if (!top || e.openedAt > top.openedAt) top = e;
  }
  return top?.id ?? null;
}

/** Close the most recently opened overlay. Returns its id or null. */
export function closeTopOverlay(): string | null {
  let top: OverlayEntry | null = null;
  for (const e of overlays.values()) {
    if (!top || e.openedAt > top.openedAt) top = e;
  }
  if (!top) return null;
  top.close();
  return top.id;
}

/** Is this overlay id the current top-of-stack? */
export function isTopOverlay(id: string): boolean {
  return topOverlayId() === id;
}
